import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Ban, CheckCircle2, Clock3, RefreshCw, XCircle } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import useManagerStore from '../store/managerStore'
import { getStoredLocale, setStoredLocale } from '../lib/localeStorage'
import { hasSubscriptionChanged } from '../lib/billingStatus'
import { formatMoney } from '../lib/money'
import { getCreditStatus, getPublicOnepayPaymentStatus } from '../api/billing'

const ONEPAY_CHECKOUT_STORAGE_KEY = 'onepay_subscription_checkout'
const ONEPAY_POLL_INTERVAL_MS = 2000
const ONEPAY_MAX_POLL_ATTEMPTS = 15

const pageStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f2f4f8',
  padding: 16,
}

const cardStyle = {
  width: '100%',
  maxWidth: 520,
  background: 'white',
  borderRadius: 16,
  border: '0.5px solid #e2e8f0',
  boxShadow: '0 8px 30px rgba(15, 23, 42, 0.08)',
  padding: 24,
}

const infoRowStyle = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  borderBottom: '0.5px dashed #e2e8f0',
  padding: '8px 0',
}

// Public page (no tenant session): the payment record's own currency is the
// source of truth; formatMoney treats a missing currency as VND.
function formatPaymentAmount(amount, currency, uiLang) {
  if (!Number.isFinite(amount) || amount < 0) return ''
  return formatMoney(amount, currency, uiLang)
}

function formatDateTime(value, locale) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function normalizeStatus(rawStatus) {
  const value = String(rawStatus || '').trim().toUpperCase()
  if (value.endsWith('SUCCESS')) return 'success'
  if (value.endsWith('FAILED')) return 'failed'
  if (value.endsWith('EXPIRED')) return 'expired'
  return 'pending'
}

function readOnepayCheckout() {
  try {
    const value = JSON.parse(sessionStorage.getItem(ONEPAY_CHECKOUT_STORAGE_KEY) || 'null')
    return value && typeof value === 'object' ? value : null
  } catch {
    return null
  }
}

function OnepaySubscriptionResult() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = useAuthStore((state) => state.token)
  const storeTenantId = useManagerStore((state) => state.tenantId)
  const callbackStatus = searchParams.get('status')
  const ref = (searchParams.get('ref') || '').trim()
  const code = (searchParams.get('code') || '').trim()
  const reason = (searchParams.get('reason') || '').trim()
  const checkout = useMemo(() => readOnepayCheckout(), [])
  const tenantId = checkout?.tenantId || storeTenantId
  const isValidSuccess = callbackStatus === 'success'
    && Boolean(ref)
    && (!checkout?.txnRef || checkout.txnRef === ref)
  const initialState = callbackStatus === 'failed'
    ? 'failed'
    : isValidSuccess
      ? 'pending_confirmation'
      : 'error'
  const [viewState, setViewState] = useState(initialState)
  const lang = getStoredLocale()
  const uiLang = (i18n.language || 'en').startsWith('vi') ? 'vi' : 'en'

  useEffect(() => {
    if (initialState !== 'pending_confirmation' || !token || !tenantId || !checkout) return undefined
    let cancelled = false
    let timerId
    let attempts = 0

    async function poll() {
      attempts += 1
      try {
        const status = await getCreditStatus(tenantId)
        if (cancelled) return
        if (hasSubscriptionChanged(status, checkout)) {
          setViewState('completed')
          sessionStorage.removeItem(ONEPAY_CHECKOUT_STORAGE_KEY)
          return
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { action: 'poll_subscription_after_onepay' },
          extra: { tenantId, ref, attempt: attempts },
        })
      }
      if (cancelled) return
      if (attempts >= ONEPAY_MAX_POLL_ATTEMPTS) {
        setViewState('pending_timeout')
        return
      }
      timerId = window.setTimeout(poll, ONEPAY_POLL_INTERVAL_MS)
    }

    void poll()
    return () => {
      cancelled = true
      window.clearTimeout(timerId)
    }
  }, [checkout, initialState, ref, tenantId, token])

  const reasonKey = ['invalid_signature', 'unknown_transaction', 'config_missing'].includes(reason)
    ? reason
    : 'generic'
  const meta = viewState === 'completed'
    ? { Icon: CheckCircle2, color: '#16a34a', bg: '#ecfdf3', border: '#bbf7d0' }
    : viewState === 'failed'
      ? { Icon: XCircle, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
      : viewState === 'error'
        ? { Icon: AlertTriangle, color: '#b45309', bg: '#fffbeb', border: '#fde68a' }
        : { Icon: Clock3, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' }
  const titleKey = viewState === 'error' ? `errors.${reasonKey}.title` : `states.${viewState}.title`
  const subtitleKey = viewState === 'error' ? `errors.${reasonKey}.subtitle` : `states.${viewState}.subtitle`

  async function changeLanguage(nextLang) {
    if (!['vi', 'en'].includes(nextLang)) return
    setStoredLocale(nextLang)
    await i18n.changeLanguage(nextLang)
  }

  function goToBilling() {
    navigate(token ? `/${lang}/billing` : `/${lang}/login`)
  }

  return (
    <div style={pageStyle}>
      <div style={{ position: 'fixed', top: 16, right: 16 }}>
        <div style={{ display: 'inline-flex', border: '0.5px solid #d1d5db', borderRadius: 8, padding: 2, background: '#fff' }}>
          {['vi', 'en'].map((language) => (
            <button key={language} type="button" onClick={() => void changeLanguage(language)} style={{ border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: uiLang === language ? '#1535c8' : 'transparent', color: uiLang === language ? 'white' : '#64748b' }}>
              {language.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <meta.Icon size={56} color={meta.color} className={viewState === 'pending_confirmation' ? 'animate-pulse' : undefined} />
          <h1 style={{ marginTop: 14, fontSize: 24, lineHeight: 1.3, fontWeight: 800, color: '#0f172a' }}>{t(`onepayResult.${titleKey}`)}</h1>
          <p style={{ marginTop: 8, fontSize: 14, color: '#64748b', maxWidth: 420 }}>{t(`onepayResult.${subtitleKey}`)}</p>
        </div>
        {(ref || (viewState === 'failed' && code)) && (
          <div style={{ marginTop: 18, borderRadius: 12, border: `0.5px solid ${meta.border}`, background: meta.bg, padding: '10px 14px' }}>
            {viewState === 'failed' && code && <div style={infoRowStyle}><span style={{ color: '#64748b', fontSize: 13 }}>{t('onepayResult.labels.code')}</span><strong style={{ color: '#0f172a', fontSize: 14 }}>{code}</strong></div>}
            {ref && <div style={infoRowStyle}><span style={{ color: '#64748b', fontSize: 13 }}>{t('onepayResult.labels.ref')}</span><strong style={{ color: '#0f172a', fontSize: 14, overflowWrap: 'anywhere' }}>{ref}</strong></div>}
          </div>
        )}
        <button type="button" onClick={viewState === 'pending_timeout' ? () => window.location.reload() : goToBilling} style={{ width: '100%', marginTop: 20, borderRadius: 10, border: 'none', background: '#1535c8', color: 'white', fontWeight: 700, fontSize: 14, padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
          {viewState === 'pending_timeout' && <RefreshCw size={16} />}
          {t(`onepayResult.actions.${viewState === 'pending_timeout' ? 'refresh' : token ? viewState === 'failed' ? 'retry' : 'billing' : 'login'}`)}
        </button>
      </div>
    </div>
  )
}

function GatewayPaymentResult() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = useAuthStore((s) => s.token)
  const [loading, setLoading] = useState(false)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [paymentData, setPaymentData] = useState(null)
  const ref = useMemo(() => (searchParams.get('ref') || '').trim(), [searchParams])
  const lang = getStoredLocale()
  const uiLang = (i18n.language || 'en').startsWith('vi') ? 'vi' : 'en'

  useEffect(() => {
    let active = true
    if (!ref) {
      Promise.resolve().then(() => {
        if (!active) return
        setPaymentData(null)
        setHasLoadError(false)
      })
      return () => {
        active = false
      }
    }

    ;(async () => {
      setLoading(true)
      setHasLoadError(false)
      try {
        const data = await getPublicOnepayPaymentStatus(ref)
        if (!active) return
        setPaymentData(data ?? null)
      } catch (err) {
        if (!active) return
        Sentry.captureException(err, {
          tags: { action: 'load_payment_status', provider: 'onepay' },
          extra: { ref },
        })
        setPaymentData(null)
        setHasLoadError(true)
      }
      if (active) setLoading(false)
    })()

    return () => {
      active = false
    }
  }, [ref])

  const normalizedStatus = useMemo(
    () => normalizeStatus(paymentData?.status),
    [paymentData?.status]
  )

  const statusMeta = useMemo(() => {
    if (normalizedStatus === 'success') {
      return {
        Icon: CheckCircle2,
        iconColor: '#16a34a',
        bg: '#ecfdf3',
        border: '#bbf7d0',
      }
    }
    if (normalizedStatus === 'failed') {
      return {
        Icon: XCircle,
        iconColor: '#dc2626',
        bg: '#fef2f2',
        border: '#fecaca',
      }
    }
    if (normalizedStatus === 'expired') {
      return {
        Icon: Ban,
        iconColor: '#b45309',
        bg: '#fffbeb',
        border: '#fde68a',
      }
    }
    return {
      Icon: Clock3,
      iconColor: '#2563eb',
      bg: '#eff6ff',
      border: '#bfdbfe',
    }
  }, [normalizedStatus])

  const formattedAmount = useMemo(() => {
    const amountNumber = Number(paymentData?.amount)
    return formatPaymentAmount(amountNumber, paymentData?.currency, uiLang)
  }, [paymentData?.amount, paymentData?.currency, uiLang])
  const gatewayTransactionNo = paymentData?.onepay_transaction_no
  const gatewayResponseCode = paymentData?.gateway_response_code
  const displayLocale = uiLang === 'vi' ? 'vi-VN' : 'en-US'
  const formattedUpdatedAt = useMemo(
    () => formatDateTime(paymentData?.updated_at, displayLocale),
    [paymentData?.updated_at, displayLocale]
  )

  function goPrimaryAction() {
    if (token) {
      navigate(`/${lang}/dashboard`)
      return
    }
    navigate(`/${lang}/login`)
  }

  async function changeLanguage(nextLang) {
    if (nextLang !== 'vi' && nextLang !== 'en') return
    setStoredLocale(nextLang)
    await i18n.changeLanguage(nextLang)
  }

  return (
    <div style={pageStyle}>
      <div style={{ position: 'fixed', top: 16, right: 16 }}>
        <div style={{ display: 'inline-flex', border: '0.5px solid #d1d5db', borderRadius: 8, padding: 2, background: '#fff' }}>
          {['vi', 'en'].map((code) => {
            const active = uiLang === code
            return (
              <button
                key={code}
                type="button"
                onClick={() => {
                  void changeLanguage(code)
                }}
                style={{
                  border: 'none',
                  borderRadius: 6,
                  padding: '5px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: active ? '#1535c8' : 'transparent',
                  color: active ? 'white' : '#64748b',
                }}
              >
                {code.toUpperCase()}
              </button>
            )
          })}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <statusMeta.Icon size={56} color={statusMeta.iconColor} />
          <h1 style={{ marginTop: 14, fontSize: 24, lineHeight: 1.3, fontWeight: 800, color: '#0f172a' }}>
            {!ref
              ? t('paymentInforPage.missingRefTitle')
              : loading
                ? t('paymentInforPage.loadingTitle')
                : hasLoadError
                  ? t('paymentInforPage.errorTitle')
                  : t(`paymentInforPage.statuses.${normalizedStatus}.title`)}
          </h1>
          <p style={{ marginTop: 8, fontSize: 14, color: '#64748b', maxWidth: 420 }}>
            {!ref
              ? t('paymentInforPage.missingRefSubtitle')
              : loading
                ? t('paymentInforPage.loadingSubtitle')
                : hasLoadError
                  ? t('paymentInforPage.errors.loadFailed')
                  : t(`paymentInforPage.statuses.${normalizedStatus}.subtitle`)}
          </p>
        </div>

        {paymentData && !loading && !hasLoadError && (
          <div
            style={{
              marginTop: 18,
              borderRadius: 12,
              border: `0.5px solid ${statusMeta.border}`,
              background: statusMeta.bg,
              padding: '10px 14px',
            }}
          >
            {formattedAmount && (
              <div style={infoRowStyle}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{t('paymentInforPage.labels.amount')}</span>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{formattedAmount}</strong>
              </div>
            )}
            {paymentData.ref && (
              <div style={infoRowStyle}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{t('paymentInforPage.labels.ref')}</span>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{paymentData.ref}</strong>
              </div>
            )}
            {gatewayTransactionNo && (
              <div style={infoRowStyle}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{t('paymentInforPage.labels.transactionNo')}</span>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{gatewayTransactionNo}</strong>
              </div>
            )}
            {gatewayResponseCode && (
              <div style={infoRowStyle}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{t('paymentInforPage.labels.responseCode')}</span>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{gatewayResponseCode}</strong>
              </div>
            )}
            {paymentData.updated_at && (
              <div style={infoRowStyle}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{t('paymentInforPage.labels.updatedAt')}</span>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{formattedUpdatedAt}</strong>
              </div>
            )}
            <div style={infoRowStyle}>
              <span style={{ color: '#64748b', fontSize: 13 }}>{t('paymentInforPage.labels.ipnReceivedAt')}</span>
              <strong style={{ color: '#0f172a', fontSize: 14 }}>
                {paymentData.ipn_received_at || t('paymentInforPage.notAvailable')}
              </strong>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={goPrimaryAction}
          style={{
            width: '100%',
            marginTop: 20,
            borderRadius: 10,
            border: 'none',
            background: '#1535c8',
            color: 'white',
            fontWeight: 700,
            fontSize: 14,
            padding: '12px 16px',
            cursor: 'pointer',
          }}
        >
          {token ? t('paymentInforPage.actions.goDashboard') : t('paymentInforPage.actions.goLogin')}
        </button>
      </div>
    </div>
  )
}

export default function PaymentSuccessPage() {
  const { pathname } = useLocation()
  return pathname.includes('/payment/onepay/result')
    ? <OnepaySubscriptionResult />
    : <GatewayPaymentResult />
}
